import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DrupalRESTService } from './drupal-rest.service';
import { EntityRESTService } from './entity-rest.service';
import { RecurrenceConfig } from '../components/forms/standing-series/standing-series.component';
import { LoggerService } from './logger.service';

/**
 * Inputs needed to create + generate a standing series. The two event dialogs
 * (lesson, services, and soon group_lesson, schedules) differ only in a few
 * of these, so they live here as parameters rather than in duplicated
 * per-component code.
 */
export interface StandingSeriesSaveOptions {
  /** Child-event bundle the series generates. */
  bundle: 'lesson' | 'group_lesson' | 'services' | 'schedules';
  /** Word used in the success snackbar, e.g. 'appointments' or 'services'. */
  noun: string;
  /**
   * The operator (instructor/executive) uid. Stored on the series as
   * field_instructor regardless of bundle; the backend's create path maps it
   * to field_executive / field_schedule_instructor per bundle.
   */
  operatorId: string;
  /**
   * Whether to carry the attendee's enrollment onto the series and into the
   * generate payload. Lessons do; services / group_lesson / schedules don't.
   */
  includeEnrollment: boolean;
  /** The recurrence config emitted by <app-standing-series>. */
  config: RecurrenceConfig;
  /**
   * The first attendee row from the form (field_student[0]). Schedules have no
   * attendee, pass {} (or any falsy-field object); field_students is omitted.
   */
  attendee: any;
  /**
   * Resolved numeric student account id, or '' for attendee-less bundles
   * (schedules). When empty, field_student_account is omitted from the series.
   */
  studentId: string;
  /** Active studio id. */
  studioId: string;
  /** HH:mm start time. */
  time: string;
  /** YYYY-MM-DD start date. */
  startDate: string;
  /** Duration string, e.g. '00:45'. */
  duration: string;
  /** Event/lesson/service type taxonomy term id, or '' if none. */
  typeId: string;
  /** Free-text note (field_notes) entered on the form, or '' if none. */
  notes?: string;
}

/**
 * Result of a create+generate attempt. Discriminated on `kind` so the calling
 * component can react (show conflicts, snackbar + close, or error) without the
 * service touching any UI surface.
 */
export type StandingSeriesSaveResult =
  | { kind: 'conflicts'; conflicts: any[]; seriesId: number }
  | { kind: 'created'; count: number; skipped: number; data: any }
  | { kind: 'error'; message: string; seriesId?: number };

/**
 * Orchestrates the two-step "create standing_series → generate child events"
 * flow shared by every event dialog that embeds <app-standing-series>.
 *
 * The service owns the REST choreography and payload shaping; the component
 * owns its own state (isRecurring, pendingSeriesId, …) and UI reactions
 * (snackbar, errorMessage, closeDialog). This is the single place a bug in the
 * flow gets fixed. Previously the same ~80 lines lived in each component and
 * drifted (e.g. the date_overrides omission in the retry path).
 */
@Injectable({ providedIn: 'root' })
export class StandingSeriesSaveService {

  constructor(
    private _entityRESTService: EntityRESTService,
    private _drupalRESTService: DrupalRESTService,
  ) {}

  /**
   * Extract a numeric id from an autocomplete display value like
   * "Jane Doe (123)". Returns numeric strings as-is.
   */
  extractIdFromAutocomplete(value: string): string {
    if (!value) return '';
    if (!isNaN(+value)) return value;
    const match = /\(([^)]+)\)(?!.*\()/.exec(value);
    return match ? match[1] : value;
  }

  /**
   * Build the standing_series entity payload from the save options.
   * Pure (no I/O), so it's trivially unit-testable.
   */
  buildSeriesData(opts: StandingSeriesSaveOptions): any {
    const { config } = opts;

    // Schedules carry no student/attendee at all, so they pass studentId=''.
    // Title falls back to the operator id, and field_student_account is omitted
    // entirely (the field is not required on the series).
    const title = opts.studentId
      ? `Standing - ${opts.studentId}`
      : `Standing - ${opts.bundle} (${opts.operatorId})`;

    const seriesData: any = {
      type: [{ target_id: 'standing' }],
      title: [{ value: title }],
      field_studio_reference: [{ target_id: opts.studioId }],
      // Created inactive: the /generate call switches it on (activate: true)
      // once it clears the conflict check, so a series abandoned at the
      // conflict prompt is never picked up by the nightly cron.
      field_standing_active: [{ value: false }],
      field_event_bundle: [{ value: opts.bundle }],
      field_days: config.days.map(d => ({ value: d })),
      field_time: [{ value: opts.time }],
      field_duration: [{ value: opts.duration }],
      field_repetition_frequency: [{ value: config.repetitionFrequency || 1 }],
      field_repetition_units: [{ value: config.repetitionUnits || 'weekly' }],
      field_instructor: [{ target_id: opts.operatorId }],
      field_end_mode: [{ value: config.endMode || 'date' }],
      field_start_date: [{ value: opts.startDate + 'T00:00:00' }],
      field_use_alternating_instructor: [{ value: config.alternateInstructors.length > 0 ? 1 : 0 }],
      field_materialization_window: [{ value: 6 }],
    };

    if (opts.studentId) {
      seriesData.field_student_account = [{ target_id: opts.studentId }];
    }

    if (config.endMode === 'date' && config.expirationDate) {
      seriesData.field_expiration_date = [{ value: config.expirationDate + 'T23:59:59' }];
    }

    if (opts.typeId) {
      seriesData.field_type = [{ target_id: opts.typeId }];
    }

    if (config.alternateInstructors.length > 0) {
      seriesData.field_alternate_instructors = config.alternateInstructors.map(uid => ({ target_id: uid }));
    }

    const notes = opts.notes || config.notes;
    if (notes) {
      seriesData.field_notes = [{ value: notes }];
    }

    if (config.dateOverrides && Object.keys(config.dateOverrides).length > 0) {
      seriesData.field_day_overrides = [{ value: JSON.stringify(config.dateOverrides) }];
    }

    if (opts.includeEnrollment) {
      const enrollmentId = opts.attendee?.field_enrollment;
      if (enrollmentId) {
        seriesData.field_enrollment = [{ target_id: enrollmentId }];
      }
    }

    return seriesData;
  }

  /** Build the attendee_data payload for the /generate endpoint. */
  private buildAttendeeData(opts: StandingSeriesSaveOptions): any {
    const attendeeData: any = {
      field_students: opts.attendee?.field_students || null,
      field_studio_reference: opts.studioId,
    };
    if (opts.includeEnrollment) {
      attendeeData.field_enrollment = opts.attendee?.field_enrollment || null;
    }
    return attendeeData;
  }

  /**
   * Create the series, then generate its events. Emits exactly one
   * StandingSeriesSaveResult.
   */
  createAndGenerate(opts: StandingSeriesSaveOptions): Observable<StandingSeriesSaveResult> {
    const seriesData = this.buildSeriesData(opts);

    return new Observable<StandingSeriesSaveResult>(observer => {
      this._entityRESTService.createEntity('standing_series', 'standing', seriesData).subscribe(
        (data: any) => {
          const seriesId = data?.id?.[0]?.value || data?.id;
          if (!seriesId) {
            observer.next({ kind: 'error', message: 'Failed to create standing series.' });
            observer.complete();
            return;
          }

          const skipConflictCheck = opts.config.hasPreviewConflicts || false;
          const excludedDates = opts.config.excludedDates || [];
          const dateOverrides = opts.config.dateOverrides || {};
          const eventInstructors = opts.config.eventInstructors || {};
          const body = {
            check_conflicts: !skipConflictCheck,
            activate: true,
            excluded_dates: excludedDates,
            date_overrides: dateOverrides,
            attendee_data: this.buildAttendeeData(opts),
            event_instructors: eventInstructors,
          };

          this._drupalRESTService.httpPOST(`/api/standing-series/${seriesId}/generate`, body).subscribe(
            (genData: any) => {
              if (genData?.has_conflicts) {
                observer.next({ kind: 'conflicts', conflicts: genData.conflicts || [], seriesId });
              } else if (genData?.success) {
                observer.next({
                  kind: 'created',
                  count: genData.created_count || 0,
                  skipped: excludedDates.length,
                  data,
                });
              } else {
                observer.next({ kind: 'error', message: 'Failed to generate events.', seriesId });
              }
              observer.complete();
            },
            (error: any) => {
              LoggerService.error('Generate error:', error);
              // Clean up the orphaned series and any events it created.
              this.cancel(seriesId).subscribe(() => {}, () => {});
              observer.next({ kind: 'error', message: 'Failed to generate events.', seriesId });
              observer.complete();
            },
          );
        },
        (error: any) => {
          LoggerService.error('Create series error:', error);
          observer.next({ kind: 'error', message: 'Failed to create standing series.' });
          observer.complete();
        },
      );
    });
  }

  /**
   * Re-generate an already-created series ignoring conflicts (the user clicked
   * "Generate Anyway"). Carries excluded_dates, date_overrides and
   * event_instructors so per-event edits made in the preview survive the
   * retry, the bug the old per-component copies dropped.
   */
  generateIgnoringConflicts(seriesId: number, opts: StandingSeriesSaveOptions): Observable<StandingSeriesSaveResult> {
    const excludedDates = opts.config.excludedDates || [];
    const body = {
      check_conflicts: false,
      activate: true,
      excluded_dates: excludedDates,
      date_overrides: opts.config.dateOverrides || {},
      attendee_data: this.buildAttendeeData(opts),
      event_instructors: opts.config.eventInstructors || {},
    };

    return new Observable<StandingSeriesSaveResult>(observer => {
      this._drupalRESTService.httpPOST(`/api/standing-series/${seriesId}/generate`, body).subscribe(
        (data: any) => {
          if (data?.success) {
            observer.next({ kind: 'created', count: data.created_count || 0, skipped: excludedDates.length, data });
          } else {
            observer.next({ kind: 'error', message: 'Failed to generate events.', seriesId });
          }
          observer.complete();
        },
        (error: any) => {
          LoggerService.error('Generate error:', error);
          observer.next({ kind: 'error', message: 'Failed to generate events.', seriesId });
          observer.complete();
        },
      );
    });
  }

  /** Deactivate a series and delete its future events (cleanup / cancel). */
  cancel(seriesId: number): Observable<any> {
    return this._drupalRESTService.httpPOST(
      `/api/standing-series/${seriesId}/deactivate`,
      { delete_future: true },
    );
  }
}
